/**
Copyright Highway9 Networks Inc.
*/
import { TemplateProperty } from "./accessConfig"
import { DataObject } from "./dataObject"

export interface AlertProfile extends DataObject {
  name: string
  batchInterval?: number
  sources: Source[]
  alertMode: AlertMode
  alertTypes: AlertTypesSelections[]
  outputPlugins?: OutputPlugin[]
  plmnList?: string[]

  isOperator?: boolean
  isEnabled?: boolean
  alertSeverity: string[]
  runtimeInfo?: {
    lastGeneratedAlertTime?: number;
    lastGeneratedAlertStatus?: {
      EMAIL?: LastGeneratedStatus;
      TEAMS?: LastGeneratedStatus;
      SLACK?: LastGeneratedStatus;
      WEBHOOK?: LastGeneratedStatus;
    }
  }
}

type SelectionType = 'PARENT_AND_ALL_CHILDREN' | 'PARENT_AND_SOME_CHILDREN' | 'SELF_ONLY'
interface Object {
  object: {
    id: string
    name: string
  }
  selectionType: SelectionType
}

interface Source {
  sourceType: "radio" | "edge" | "device" | "clock"
  selectionType: SelectionType
  objects: Object[]
  parentId?: string
}


interface AlertMode {
  frequency?: string
  schedule?: string; // cron expression
  alertModeType: string
  timezone?: string
}

interface LastGeneratedStatus {
  failed: string[]
  success: number;
  total: number;
}

// Note: keep literals in sync with OutputPlugin union
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type OutputPluginType = 'EMAIL' | 'TEAMS' | 'SLACK' | 'WEBHOOK' | 'SUMO_LOGIC' | 'SOLARWINDS' | 'SPLUNK' | 'DATADOG' | 'OPEN_TELEMETRY'


export type OutputPlugin = {
  type: OutputPluginType
  isEnabled: boolean
  config: Record<string, any>
}


export type AlertType = 'Infrastructure' | 'KPI' | 'Change Notifications';
export interface AlertTypesSelections {
  alertType: AlertType
  selectionType: SelectionType
  alerts: { alertId: number, severity?: string }[]
}

export type OutputPluginTemplate = {
  type: OutputPluginType
  enabled: boolean
  properties: TemplateProperty[]
}

export const defaultAlertProfile: AlertProfile = {
  name: "Alert Profile",
  sources: [
    {
      sourceType: "radio",
      selectionType: "PARENT_AND_ALL_CHILDREN",
      objects: []
    },
    {
      sourceType: "edge",
      selectionType: "PARENT_AND_ALL_CHILDREN",
      objects: []
    },
    {
      sourceType: "device",
      selectionType: "PARENT_AND_ALL_CHILDREN",
      objects: []
    },
    {
      sourceType: "clock",
      selectionType: "PARENT_AND_ALL_CHILDREN",
      objects: []
    }
  ],
  alertMode: {
    alertModeType: "IMMEDIATE",
  },
  batchInterval: 1,
  alertTypes: [
    {
      alertType: 'Infrastructure',
      selectionType: 'PARENT_AND_ALL_CHILDREN',
      alerts: []
    },
  ],
  outputPlugins: [{
    type: "EMAIL",
    isEnabled: true,
    config: {}
  }],
  alertSeverity: ['CRITICAL'],
  isEnabled: true,
}


export const defaultOutputPluginTemplate: OutputPluginTemplate[] = [
  {
    "properties": [
      {
        "description": "Email Addresses",
        "enumValues": null,
        "name": "Email Addresses",
        "objectProperties": null,
        "type": "STRING_ARRAY",
        "isRequired": true
      }
    ],
    "enabled": true,
    "type": "EMAIL"
  },
  {
    "properties": [
      {
        "description": "Teams Webhook URL",
        "enumValues": null,
        "name": "Teams Webhook URL",
        "objectProperties": null,
        "type": "STRING_ARRAY",
        "isRequired": true
      }
    ],
    "enabled": true,
    "type": "TEAMS"
  },
  {
    "properties": [
      {
        "description": "Slack Webhook URL",
        "enumValues": null,
        "name": "Slack Webhook URL",
        "objectProperties": null,
        "type": "STRING_ARRAY",
        "isRequired": true
      }
    ],
    "enabled": true,
    "type": "SLACK"
  },
  {
    "enabled": true,
    "properties": [
      {
        "description": "Webhook URL",
        "enumValues": null,
        "name": "Webhook URL",
        "objectProperties": null,
        "type": "STRING",
        "isRequired": true
      },
      {
        "description": "List of headers",
        "enumValues": null,
        "name": "Headers",
        "isRequired": false,
        "objectProperties": [
          {
            "description": "Key",
            "enumValues": null,
            "name": "Key",
            "objectProperties": null,
            "type": "STRING",
            "isRequired": false
          },
          {
            "description": "Value",
            "enumValues": null,
            "name": "Value",
            "objectProperties": null,
            "type": "STRING",
            "isRequired": false
          }
        ],
        "type": "OBJECT_ARRAY"
      },
      {
        "description": "Enable HMAC Authentication",
        "enumValues": null,
        "name": "Enable HMAC Authentication",
        "objectProperties": null,
        "type": "BOOLEAN",
        "isRequired": false
      },
      {
        "description": "Shared Secret",
        "enumValues": null,
        "name": "Shared Secret",
        "objectProperties": null,
        "type": "STRING",
        "isRequired": false
      }
    ],
    "type": "WEBHOOK"
  }
]